import { Reducer } from "../../lib/redux";
import constants from "../../constants";
import { extend } from "../../utils/vanilla";
import { MobileActions } from "./actions";
import {
    IBackground,
    IDriverActionNecessary,
    IGps,
    IGpsData,
    ILoginRequiredState,
    IMobilePageState,
    IMobileStore,
    IViewedLog,
    LoginRequiredStatus,
    MobileActionTypes,
    DriverActionTypes,
    NetworkStates,
    ISyncInterval,
    ILastKnownSpeed,
    IIamUserRefreshPayload,
    IIamUserSessionInvalidatedPayload
} from "./types";

export let defaultMobile: IMobileStore = {
    pageState: {
        title: constants.APP_NAME,
        activeSection: "home",
        pageLevel: 0,
        isLocked: false,
        name: "state"
    },
    background: false,
    online: NetworkStates.ONLINE,
    embed: false,
    eldMalfunction: false,
    eldDiagnostic: false,
    diagnosticViewed: undefined,
    newUpcomingViolation: false,
    driverActionNecessary: {
        isNecessary: false,
        actionType: DriverActionTypes.Default
    },
    language: "en",
    newMessage: false,
    loginRequired: {
        status: LoginRequiredStatus.Default
    },
    pendoResourceCenterAvailable: false,
    tripCategorizationSyncInterval: { fromDate: undefined, toDate: undefined },
    iam: {},
    iamSessionInvalidated: {},
    dvirDurationStartTime: undefined
};

const setPageState = (state: IMobileStore, payload: IMobilePageState): IMobileStore => {
    let newPageState = extend(true, {}, state.pageState, payload || {});

    return {
        ...state,
        pageState: newPageState
    };
};

const setCharging = (state: IMobileStore, payload?: boolean): IMobileStore => {
    if (state.charging === payload) {
        return state;
    }

    return {
        ...state,
        charging: payload
    };
};

const setBatteryLevel = (state: IMobileStore, payload?: number): IMobileStore => {
    if (state.batteryLevel === payload) {
        return state;
    }
    return {
        ...state,
        batteryLevel: payload
    };
};

const setBackground = (state: IMobileStore, payload?: IBackground): IMobileStore => {
    if (!payload || payload.isBackground === state.background) {
        return state;
    }
    return {
        ...state,
        background: payload.isBackground
    };
};

const setOnline = (state: IMobileStore, payload?: NetworkStates): IMobileStore => {
    if (state.online === payload) {
        return state;
    }
    let newState = {
        ...state,
        online: payload
    };

    return newState;
};

const setDeviceCommunicating = (state: IMobileStore, payload?: boolean): IMobileStore => {
    if (state.deviceCommunicating === payload) {
        return state;
    }

    return {
        ...state,
        deviceCommunicating: payload,
        // clear lastDeviceCommunicationDateTime when setting deviceCommunicating to undefined
        lastDeviceCommunicationDateTime: payload === undefined ? undefined : state.lastDeviceCommunicationDateTime
    };
};

const setLastDeviceCommunicationDateTime = (state: IMobileStore, payload?: string): IMobileStore => {
    if (state.lastDeviceCommunicationDateTime === payload) {
        return state;
    }

    return {
        ...state,
        lastDeviceCommunicationDateTime: payload
    };
};

const setGps = (state: IMobileStore, payload: IGps): IMobileStore => {
    // Triggered when gps detects position change {position, speed}
    const lastPosition = payload.position ? extend(true, {}, payload.position) : undefined;
    return {
        ...state,
        gpsConnected: payload.isConnected,
        lastPosition: lastPosition
    };
};


const setEmbed = (state: IMobileStore, payload?: boolean): IMobileStore => {
    // Triggered when the app moves between embedded mode and normal mode (true/false)
    return {
        ...state,
        embed: payload
    };
};

const setEldMalfunction = (state: IMobileStore, payload?: boolean): IMobileStore => {
    return {
        ...state,
        eldMalfunction: payload
    };
};

const setNewUpcomingViolation = (state: IMobileStore, payload?: boolean): IMobileStore => {
    return {
        ...state,
        newUpcomingViolation: payload
    };
};

const setEldDiagnostic = (state: IMobileStore, payload?: boolean): IMobileStore => {
    return {
        ...state,
        eldDiagnostic: payload
    };
};

const setDiagnosticViewed = (state: IMobileStore, payload?: boolean): IMobileStore => {
    return {
        ...state,
        diagnosticViewed: payload
    };
};

const setLanguage = (state: IMobileStore, payload?: string): IMobileStore => {
    return {
        ...state,
        language: payload
    };
};

const setDriverActionNecessary = (state: IMobileStore, payload: IDriverActionNecessary): IMobileStore => {
    let isDriverActionNecessary = payload.isNecessary;

    if (state.driverActionNecessary?.isNecessary === isDriverActionNecessary) {
        return state;
    }

    return {
        ...state,
        driverActionNecessary: extend(true, {}, payload)
    };
};

const setIoxAttached = (state: IMobileStore, payload?: boolean): IMobileStore => {
    if (state.ioxAttached === payload) {
        return state;
    }

    return {
        ...state,
        ioxAttached: payload
    };
};

const setGpsData = (state: IMobileStore, payload?: IGpsData): IMobileStore => {
    const gpsData = payload ? extend(true, {}, payload) : undefined;
    return {
        ...state,
        gpsData: gpsData
    };
};

const setLastViewedLog = (state: IMobileStore, payload?: IViewedLog): IMobileStore => {
    const lastViewedLog = payload ? extend(true, {}, payload) : undefined;
    return {
        ...state,
        lastViewedLog: lastViewedLog
    };
};

const setNewMessage = (state: IMobileStore, payload?: boolean) => {
    return {
        ...state,
        newMessage: payload
    };
};

const setPreviousPage = (state: IMobileStore, payload?: string) => {
    return {
        ...state,
        previousPage: payload
    };
};

const setLoginRequired = (state: IMobileStore, payload: ILoginRequiredState) => {
    return {
        ...state,
        loginRequired: extend(true, {}, payload)
    };
};

const setPcLimitWarningMessage = (state: IMobileStore, payload?: string): IMobileStore => {
    return {
        ...state,
        pcLimitWarningMessage: payload
    };
};

const setPageLoaded = (state: IMobileStore, payload: string) => {
    let previousPage = state.currentPage;

    if (previousPage && previousPage === payload) {
        return state;
    }

    if (previousPage && previousPage === "ui/login") {
        let loginRequiredState = setLoginRequired(state, { status: LoginRequiredStatus.Default });
        return {
            ...state,
            currentPage: payload,
            loginRequired: extend(true, {}, loginRequiredState.loginRequired)
        };
    }

    return {
        ...state,
        currentPage: payload
    };
};

const setPendoResourceCenterAvailable = (state: IMobileStore, payload: boolean): IMobileStore => {
    return {
        ...state,
        pendoResourceCenterAvailable: payload
    };
};

const setTripCategorizationSyncInterval = (state: IMobileStore, payload: ISyncInterval): IMobileStore => {
    return {
        ...state,
        tripCategorizationSyncInterval: extend(true, {}, payload)
    };
};

const setLastKnownSpeed = (state: IMobileStore, payload: ILastKnownSpeed): IMobileStore => {
    return {
        ...state,
        lastKnownSpeed: extend(true, {}, payload)
    };
};

const setIamRefreshState = (state: IMobileStore, payload: IIamUserRefreshPayload): IMobileStore => {
    if (!state.iam) {
        state.iam = {};
    }

    state.iam[payload.key] = payload.isRefreshing;
    return state;
};

const setIamSessionInvalidatedState = (state: IMobileStore, payload: IIamUserSessionInvalidatedPayload): IMobileStore => {
    if (!state.iamSessionInvalidated) {
        state.iamSessionInvalidated = {};
    }

    state.iamSessionInvalidated[payload.key] = payload.isInvalidated;
    return state;
};


const setDvirDurationStartTime = (state: IMobileStore, payload?: Date): IMobileStore => {
    if (state.dvirDurationStartTime === payload) {
        return state;
    }

    return {
        ...state,
        dvirDurationStartTime: payload
    };
};

const mobileActionTypesDictionary: EnumDictionary<MobileActionTypes, Function> = {
    [MobileActionTypes.SET_PAGESTATE]: setPageState,
    [MobileActionTypes.SET_CHARGING]: setCharging,
    [MobileActionTypes.SET_BATTERYLEVEL]: setBatteryLevel,
    [MobileActionTypes.SET_BACKGROUND]: setBackground,
    [MobileActionTypes.SET_ONLINE]: setOnline,
    [MobileActionTypes.SET_DEVICE_COMMUNICATING]: setDeviceCommunicating,
    [MobileActionTypes.SET_LAST_DEVICE_COMMUNICATION_DATETIME]: setLastDeviceCommunicationDateTime,
    [MobileActionTypes.SET_GPS]: setGps,
    [MobileActionTypes.SET_EMBED]: setEmbed,
    [MobileActionTypes.SET_ELDMALFUNCTION]: setEldMalfunction,
    [MobileActionTypes.SET_NEWUPCOMINGVIOLATION]: setNewUpcomingViolation,
    [MobileActionTypes.SET_ELDDIAGNOSTIC]: setEldDiagnostic,
    [MobileActionTypes.SET_DIAGNOSTICVIEWED]: setDiagnosticViewed,
    [MobileActionTypes.SET_LANGUAGE]: setLanguage,
    [MobileActionTypes.SET_DRIVERACTIONNECESSARY]: setDriverActionNecessary,
    [MobileActionTypes.SET_IOX_ATTACHED]: setIoxAttached,
    [MobileActionTypes.SET_GPS_DATA]: setGpsData,
    [MobileActionTypes.SET_LAST_VIEWED_LOG]: setLastViewedLog,
    [MobileActionTypes.SET_NEW_MESSAGE]: setNewMessage,
    [MobileActionTypes.SET_PREVIOUS_PAGE]: setPreviousPage,
    [MobileActionTypes.SET_LOGIN_REQUIRED]: setLoginRequired,
    [MobileActionTypes.SET_PCLIMIT_WARNING]: setPcLimitWarningMessage,
    [MobileActionTypes.SET_PAGE_LOADED]: setPageLoaded,
    [MobileActionTypes.SET_PENDO_RESOURCE_CENTER_AVAILABLE]: setPendoResourceCenterAvailable,
    [MobileActionTypes.SET_TRIP_CATEGORIZATION_SYNC_INTERVAL]: setTripCategorizationSyncInterval,
    [MobileActionTypes.SET_LAST_KNOWN_SPEED]: setLastKnownSpeed,
    [MobileActionTypes.SET_IAM_USER_REFRESH_STATE]: setIamRefreshState,
    [MobileActionTypes.SET_IAM_SESSION_INVALIDATED_STATE]: setIamSessionInvalidatedState,
    [MobileActionTypes.SET_DVIR_DURATION_START_TIME]: setDvirDurationStartTime
};

export let mobile: Reducer<IMobileStore, MobileActions> = (state = defaultMobile, action: MobileActions) => {
    let stateMethod = mobileActionTypesDictionary[action.type];
    return stateMethod ? stateMethod(state, action.payload) : state;
};
